export interface SpecQuestionOption {
  label: string;
  description?: string;
}

export interface SpecQuestionItem {
  question: string;
  options: SpecQuestionOption[];
  recommended: number;
}

/** The result of an ask_member call: what the panel and the transcript render. */
export interface SpecQuestionSet {
  type: 'spec-question';
  questions: SpecQuestionItem[];
}

const ONE_LINE = /^[^\n\r]+$/;

/** What a skipped question's answer reads as, to the model and in the summary. */
export const SPEC_SKIPPED_ANSWER = 'Skipped';

function readItem(value: unknown): SpecQuestionItem | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (typeof record.question !== 'string') return null;
  const question = record.question.trim();
  if (question.length === 0 || question.length > 240 || !ONE_LINE.test(question)) return null;
  if (!Array.isArray(record.options) || record.options.length < 2 || record.options.length > 4) {
    return null;
  }
  const recommended = record.recommended;
  if (typeof recommended !== 'number' || !Number.isInteger(recommended)) return null;
  if (recommended < 0 || recommended >= record.options.length) return null;
  const options: SpecQuestionOption[] = [];
  const seen = new Set<string>();
  for (const raw of record.options) {
    if (typeof raw !== 'object' || raw === null) return null;
    const { label, description } = raw as { label?: unknown; description?: unknown };
    if (typeof label !== 'string') return null;
    const trimmed = label.trim();
    if (trimmed.length === 0 || trimmed.length > 60 || !ONE_LINE.test(trimmed)) return null;
    if (seen.has(trimmed.toLowerCase())) return null;
    seen.add(trimmed.toLowerCase());
    if (description !== undefined && typeof description !== 'string') return null;
    const detail = description?.trim() ?? '';
    if (detail.length > 160) return null;
    options.push(detail.length > 0 ? { label: trimmed, description: detail } : { label: trimmed });
  }
  return { question, options, recommended };
}

/** A question set under the worker's rules (apps/spec/src/ask-member.ts), or null. */
export function readSpecQuestionSet(value: unknown): SpecQuestionSet | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  if (record.type !== 'spec-question') return null;
  // The first build stored one question flat; those preview chats read as a set of one.
  if (!('questions' in record)) {
    const item = readItem(record);
    return item ? { type: 'spec-question', questions: [item] } : null;
  }
  if (!Array.isArray(record.questions)) return null;
  if (record.questions.length < 1 || record.questions.length > 4) return null;
  const questions: SpecQuestionItem[] = [];
  const seen = new Set<string>();
  for (const raw of record.questions) {
    const item = readItem(raw);
    if (!item || seen.has(item.question.toLowerCase())) return null;
    seen.add(item.question.toLowerCase());
    questions.push(item);
  }
  return { type: 'spec-question', questions };
}

/** The first ask_member part in a message's content. */
export function questionSetIn(
  content: readonly unknown[],
): { toolCallId: string; questionSet: SpecQuestionSet } | null {
  for (const part of content) {
    if (typeof part !== 'object' || part === null) continue;
    const record = part as {
      type?: unknown;
      toolName?: unknown;
      toolCallId?: unknown;
      result?: unknown;
    };
    if (record.type !== 'tool-call' || record.toolName !== 'ask_member') continue;
    const questionSet = readSpecQuestionSet(record.result);
    if (questionSet && typeof record.toolCallId === 'string') {
      return { toolCallId: record.toolCallId, questionSet };
    }
  }
  return null;
}

function oneLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

/** The member's answer as sent: each question then its answer, pairs separated by a blank line. */
export function formatSpecAnswers(
  questionSet: SpecQuestionSet,
  answers: readonly (string | null)[],
): string {
  return questionSet.questions
    .map((item, index) => `${item.question}\n${oneLine(answers[index] ?? SPEC_SKIPPED_ANSWER)}`)
    .join('\n\n');
}

/** The answers in `text` when it is exactly the answer to this set, else null. */
export function parseSpecAnswers(questionSet: SpecQuestionSet, text: string): string[] | null {
  const answers: string[] = [];
  let rest = text;
  for (const [index, item] of questionSet.questions.entries()) {
    const head = `${item.question}\n`;
    if (!rest.startsWith(head)) return null;
    rest = rest.slice(head.length);
    const next = questionSet.questions[index + 1];
    const end = next ? rest.indexOf(`\n\n${next.question}\n`) : rest.length;
    if (end < 0) return null;
    const answer = rest.slice(0, end);
    if (!ONE_LINE.test(answer)) return null;
    answers.push(answer);
    rest = next ? rest.slice(end + 2) : '';
  }
  return answers;
}
